import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { fetchSessionReviewInteractions } from '../../services/sessionReviewService';
import { getGenderTextColor } from '../../utils/genderColors';
import { Text } from '../common/Typography';
import { EmptyState, LoadingState, StatusBadge } from '../common';

const CompactStat = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-2 sm:block sm:text-right">
    <span className="text-xs text-text-muted sm:sr-only">{label}</span>
    <span className="text-sm font-semibold tabular-nums text-text-primary">{value}</span>
  </div>
);

const SessionReviewInteractionsTable = ({ sessionId, simulationData = null, filterPlayerIds, layout = 'default' }) => {
  // Stabilize filter array to avoid rerunning effects when parent doesn't pass one
  const emptyFilterRef = useRef([]);
  const effectiveFilterIds = useMemo(
    () => filterPlayerIds ?? emptyFilterRef.current,
    [filterPlayerIds]
  );
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apply player filter to fetched interactions
  const displayedInteractions = useMemo(() => {
    if (!effectiveFilterIds || effectiveFilterIds.length === 0) return interactions;
    return interactions.filter(interaction => (
      effectiveFilterIds.includes(interaction.player1_id) ||
      effectiveFilterIds.includes(interaction.player2_id)
    ));
  }, [interactions, effectiveFilterIds]);

  const sortedInteractions = useMemo(
    () => [...displayedInteractions].sort((a, b) => b.total_interactions - a.total_interactions),
    [displayedInteractions]
  );

  const loadInteractions = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const data = await fetchSessionReviewInteractions(sessionId);
      setInteractions(data);
    } catch (err) {
      console.error('Error loading session review interactions:', err);
      setError('Failed to load player interactions data');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (simulationData) {
      // Use simulation data directly
      let filteredInteractions = simulationData;
      
      // Apply player filter if provided
      if (effectiveFilterIds.length > 0) {
        filteredInteractions = simulationData.filter(interaction => {
          // Check if either player1 or player2 is in the filter
          return effectiveFilterIds.includes(interaction.player1_id) || 
                 effectiveFilterIds.includes(interaction.player2_id);
        });
      }
      
      setInteractions(filteredInteractions);
      setLoading(false);
      setError(null);
    } else if (sessionId) {
      loadInteractions();
    } else {
      setLoading(false);
    }
  }, [sessionId, simulationData, effectiveFilterIds, loadInteractions]);

  if (loading) {
    return layout === 'compact'
      ? <LoadingState message="Loading pairings…" />
      : (
        <div className="text-center py-6 text-text-muted">
          Loading player interactions...
        </div>
      );
  }

  if (error) {
    return layout === 'compact'
      ? <EmptyState title="Could not load pairings" description={error} />
      : (
        <div className="text-center py-6 text-danger">
          {error}
        </div>
      );
  }

  if (displayedInteractions.length === 0) {
    return layout === 'compact'
      ? <EmptyState title="No pairings to show" description="No player interactions found for this session." />
      : (
        <div className="text-center py-6 text-text-muted">
          No player interactions found for this session.
        </div>
      );
  }

  if (layout === 'compact') {
    return (
      <div>
        <Text className="mb-3 text-xs text-text-secondary">
          {sortedInteractions.length} pairing{sortedInteractions.length === 1 ? '' : 's'}, sorted by most
          games together
        </Text>

        <div className="overflow-hidden rounded-xl border border-border-medium">
          <div
            className="hidden gap-3 border-b border-border-medium bg-surface-subtle px-4 py-2 text-[10px] font-medium uppercase tracking-wide text-text-muted sm:grid sm:grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_4.5rem]"
            aria-hidden
          >
            <span>Players</span>
            <span className="text-right">Same team</span>
            <span className="text-right">Opponents</span>
            <span className="text-right">Total</span>
          </div>
          <ul className="divide-y divide-border-medium">
            {sortedInteractions.map((interaction) => {
              const frequent = interaction.total_interactions >= 3;
              return (
                <li
                  key={`${interaction.player1_id}_${interaction.player2_id}`}
                  className={`px-3 py-2.5 sm:px-4 ${frequent ? 'bg-warning-light/15' : 'bg-surface'}`}
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_5.5rem_5.5rem_4.5rem] sm:items-center sm:gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold">
                        <span className={getGenderTextColor(interaction.player1_gender)}>
                          {interaction.player1_name || 'Unknown'}
                        </span>
                        <span className="text-xs font-normal text-text-muted" aria-hidden>
                          ↔
                        </span>
                        <span className={getGenderTextColor(interaction.player2_gender)}>
                          {interaction.player2_name || 'Unknown'}
                        </span>
                        {interaction.altered_games_count > 0 ? (
                          <StatusBadge variant="warning" size="small">
                            {interaction.altered_games_count} altered
                          </StatusBadge>
                        ) : null}
                      </div>
                    </div>
                    <p className="text-xs text-text-secondary sm:hidden">
                      {interaction.games_played_together} same team · {interaction.games_played_against} opponents · {interaction.total_interactions} total
                    </p>
                    <div className="hidden sm:block">
                      <CompactStat label="Same team" value={interaction.games_played_together} />
                    </div>
                    <div className="hidden sm:block">
                      <CompactStat label="Opponents" value={interaction.games_played_against} />
                    </div>
                    <div className="hidden sm:block">
                      <CompactStat label="Total" value={interaction.total_interactions} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="lg:hidden space-y-3">
        {displayedInteractions.map((interaction) => {
          const asterisks = interaction.altered_games_count > 0
            ? '*'.repeat(interaction.altered_games_count)
            : '';
          return (
            <div
              key={`${interaction.player1_id}_${interaction.player2_id}`}
              className="rounded-lg border border-border-medium bg-surface p-4 space-y-3"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium">
                <span className={getGenderTextColor(interaction.player1_gender)}>
                  {interaction.player1_name || 'Unknown'}
                </span>
                {asterisks && (
                  <span className="text-warning font-bold" title={`${interaction.altered_games_count} altered game(s)`}>
                    {asterisks}
                  </span>
                )}
                <span className="text-text-muted text-xs">vs</span>
                <span className={getGenderTextColor(interaction.player2_gender)}>
                  {interaction.player2_name || 'Unknown'}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <div className="text-xs text-text-muted">Together</div>
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark mt-0.5">
                    {interaction.games_played_together}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Against</div>
                  <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark mt-0.5">
                    {interaction.games_played_against}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-text-muted">Total</div>
                  <div className="font-semibold text-text-primary mt-0.5">{interaction.total_interactions}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    <div className="hidden lg:block overflow-x-auto">
      <table className="min-w-full divide-y divide-border-medium">
        <thead className="bg-surface-subtle">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Player 1
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
              Player 2
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Played Together
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Played Against
            </th>
            <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase tracking-wider">
              Total Interactions
            </th>
          </tr>
        </thead>
        <tbody className="bg-surface divide-y divide-border-medium">
          {displayedInteractions.map((interaction) => {
            // Generate asterisks based on altered games count
            const asterisks = interaction.altered_games_count > 0 
              ? '*'.repeat(interaction.altered_games_count) 
              : '';
            
            return (
              <tr key={`${interaction.player1_id}_${interaction.player2_id}`} className="hover:bg-surface-subtle">
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                  <span className={getGenderTextColor(interaction.player1_gender)}>
                    {interaction.player1_name || 'Unknown'}
                  </span>
                  {asterisks && (
                    <span className="text-warning font-bold ml-1" title={`${interaction.altered_games_count} altered game(s)`}>
                      {asterisks}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                  <span className={getGenderTextColor(interaction.player2_gender)}>
                    {interaction.player2_name || 'Unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-light text-success-dark">
                    {interaction.games_played_together}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-light text-info-dark">
                    {interaction.games_played_against}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-text-primary">
                  {interaction.total_interactions}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </div>
  );
};

export default SessionReviewInteractionsTable;

